import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';

import { ChatWidget as ChatWidgetComponent } from './components/chat/ChatWidget';
import AdminPage from './components/admin/AdminPage';
import { ChatLauncher } from './components/chat/ChatLauncher';
import { useChat } from './hooks/useChat';

const STATIC_URL = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' && window.supportlyChatConfig?.staticUrl) || '';

const Spinner = styled.div`
  width: 50px;
  height: 50px;
  border: 5px solid #f3f3f3;
  border-top: 5px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 20px auto;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  font-family: Arial, sans-serif;
`;

const ConfigValidator: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  let urlName = '';
  let urlSessionId = '';
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    urlName = params.get('name') || '';
    urlSessionId = params.get('sessionId') || '';
  }

  const config = window.supportlyChatConfig || {};
  const name = urlName || config.name || 'Guest';

  let sessionId = urlSessionId || config.sessionId;
  if (!sessionId && typeof window !== 'undefined') {
    const stored = localStorage.getItem('supportlySessionId');
    if (stored) {
      sessionId = stored;
    } else {
      sessionId = uuidv4();
      localStorage.setItem('supportlySessionId', sessionId as string);
    }
  }
  if (!sessionId) sessionId = 'default-session-id';

  const isEmbed = typeof window !== 'undefined' && window.isSupportlyEmbed;

  if (!isEmbed && (!name || !sessionId || name.trim() === '' || sessionId.trim() === '')) {
    return (
      <LoadingContainer>
        <div>Chat configuration 'name' and 'sessionId' are missing. Please set window.supportlyChatConfig.</div>
      </LoadingContainer>
    );
  }

  return <>{children}</>;
};

const ParameterValidator: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const name = query.get('name');
  const sessionId = query.get('sessionId');

  if (!name || !sessionId || name.trim() === '' || sessionId.trim() === '') {
    return (
      <LoadingContainer>
        <div>Required URL parameters 'name' and 'sessionId' are missing or empty.</div>
      </LoadingContainer>
    );
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/admin/*" element={<AdminPage />} />
        <Route path="/chat" element={
          <ParameterValidator>
            <DirectChatPage />
          </ParameterValidator>
        } />
        <Route path="/*" element={
          <ConfigValidator>
            <ChatHost />
          </ConfigValidator>
        } />
      </Routes>
    </BrowserRouter>
  );
};

const DirectChatPage: React.FC = () => {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const name = query.get('name') || 'Guest';
  const sessionId = query.get('sessionId') || uuidv4();

  const {
    socket,
    messages,
    typingUsers,
    isChatOpen,
    unreadCount,
    soundEnabled,
    audioRef,
    handleSendMessage,
    openChat,
    closeChat,
    setSoundEnabled,
    activeLimitConsent,
    submittingConsentAnswer,
    submitConsentAnswer,
    archiveConversations,
    activeConversationDate,
    openArchivedConversation,
    openActiveConversation,
  } = useChat({ name, sessionId });
  const hasOpenedChatRef = useRef(false);

  // For direct link, chat should be open by default
  useEffect(() => {
    if (hasOpenedChatRef.current) return;
    hasOpenedChatRef.current = true;
    openChat();
  }, [openChat]);

  return (
    <>
      {isChatOpen ? (
        <ChatWidgetComponent
          socket={socket}
          messages={messages}
          onSendMessage={handleSendMessage}
          onConsentAnswer={submitConsentAnswer}
          consentQuestion={activeLimitConsent.nextQuestion?.questionText || null}
          consentSubmitting={submittingConsentAnswer}
          activeConsentCase={{
            hasActiveCase: activeLimitConsent.hasActiveCase,
            status: activeLimitConsent.status,
          }}
          archiveConversations={archiveConversations}
          activeConversationDate={activeConversationDate}
          onOpenArchivedConversation={openArchivedConversation}
          onOpenActiveConversation={openActiveConversation}
          onTyping={() => socket?.emit('typing_start', { sessionId })}
          onStopTyping={() => socket?.emit('typing_stop', { sessionId })}
          currentUserId={sessionId}
          typingUsers={typingUsers}
          onClose={closeChat}
          sessionId={sessionId}
          soundEnabled={soundEnabled}
          setSoundEnabled={setSoundEnabled}
          error={null}
          userName={name}
          fullScreen={true} // Open in full screen for direct link
        />
      ) : (
         // You can show a launcher or a loading spinner here before it opens
        <ChatLauncher onClick={openChat} unreadCount={unreadCount} />
      )}
      <audio ref={audioRef} src={`${STATIC_URL}/notification.mp3`} preload="auto" />
    </>
  );
};

const ChatHost: React.FC = () => {
  const config = window.supportlyChatConfig || {};
  const name = config.name || 'Guest';

  const getStableSessionId = () => {
    let urlSessionId = '';
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
    }
    let sessionId: string = urlSessionId || config.sessionId || '';
    if (!sessionId && typeof window !== 'undefined') {
      const stored = localStorage.getItem('supportlySessionId');
      if (stored) {
        sessionId = stored;
      } else {
        sessionId = uuidv4();
        localStorage.setItem('supportlySessionId', sessionId);
      }
    }
    return sessionId || 'default-session-id';
  };
  const sessionIdRef = useRef<string>(getStableSessionId());
  const sessionId = sessionIdRef.current;

  const {
    socket,
    messages,
    typingUsers,
    isChatOpen,
    unreadCount,
    soundEnabled,
    audioRef,
    handleSendMessage,
    openChat,
    closeChat,
    setSoundEnabled,
    activeLimitConsent,
    submittingConsentAnswer,
    submitConsentAnswer,
    archiveConversations,
    activeConversationDate,
    openArchivedConversation,
    openActiveConversation,
  } = useChat({ name, sessionId });

  return (
    <>
      {isChatOpen ? (
        <ChatWidgetComponent
          socket={socket}
          messages={messages}
          onSendMessage={handleSendMessage}
          onConsentAnswer={submitConsentAnswer}
          consentQuestion={activeLimitConsent.nextQuestion?.questionText || null}
          consentSubmitting={submittingConsentAnswer}
          activeConsentCase={{
            hasActiveCase: activeLimitConsent.hasActiveCase,
            status: activeLimitConsent.status,
          }}
          archiveConversations={archiveConversations}
          activeConversationDate={activeConversationDate}
          onOpenArchivedConversation={openArchivedConversation}
          onOpenActiveConversation={openActiveConversation}
          onTyping={() => socket?.emit('typing_start', { sessionId })}
          onStopTyping={() => socket?.emit('typing_stop', { sessionId })}
          currentUserId={sessionId}
          typingUsers={typingUsers}
          onClose={closeChat}
          sessionId={sessionId}
          soundEnabled={soundEnabled}
          setSoundEnabled={setSoundEnabled}
          error={null}
          userName={name}
          fullScreen={false}
        />
      ) : (
        <ChatLauncher onClick={openChat} unreadCount={unreadCount} />
      )}
      <audio ref={audioRef} src={`${STATIC_URL}/notification.mp3`} preload="auto" />
    </>
  );
};

export default App; 
